import { Dispatch } from 'redux';
import { State, Modals } from '../state';
import { findPledgeByUUID, userApi, calculatorAvailablePoints, isCalculatorPledgeComplete } from '../selectors';
import { userApiBasePath } from '../selectors/userApi';
import { CompetitionPledges } from '../../utils/userApiHelper';
import { addUserPoints } from './score';
import { ExtraArguments } from '../extraArguments';
import logger from '../../utils/logger';
import { trackEvent } from '../../utils/googleAnalytics';
import { patch } from './patch';
import { ActionType } from './types';
import { Story, CompetitionUserPoint } from '../../userApi';
import {
  formatShareStoryText,
  normalizeCalculatorSnapshot,
  parseCalculatorSnapshot,
  CalculatorSnapshot,
  mergeCalculatorAnswersIntoSnapshot,
  hasCategoryBreakdown,
  categorySumMt,
  snapshotTotalsMatch,
  scaleCategoriesToTotal
} from '../../utils/calculatorSnapshot';
import { renderFootprintChartDataUrl, tryCaptureIframeChart } from '../../utils/captureFootprintChart';
import {
  mergeCalculatorSnapshots,
  resolveAccurateCalculatorSnapshot,
  setLatestCalculatorInputs,
  fetchSnapshotFromCoolClimateApi
} from '../../utils/calculatorEmbeddedData';
import { dataURLToBlob } from '../../utils/image';
import { postFormData } from '../../utils/userApiHelper';
import { showActionCompletedToast } from './completeAction';
import { handleUnexpectedError } from './handleUnexpectedError';
import { toast } from './toast';
import { StyleState } from '../../utils/styles';
import { postCalculatorNavigate } from '../../utils/calculatorIframeMessages';
import { STORY_MIN_CHARS } from '../../utils/storyMinChars';

const CALCULATOR_CONTAINER_ID = 'cc__calculator';
const FOOTPRINT_RETRY_MS = 200;
const FOOTPRINT_MAX_ATTEMPTS = 8;
const CHART_CAPTURE_RETRY_MS = 150;
const CHART_CAPTURE_MAX_ATTEMPTS = 4;

export const resolveCalculatorChartPreviewUrl = async (
  snapshot: CalculatorSnapshot,
  state: State
): Promise<string> => {
  let enriched = await enrichSnapshotFromSavedAnswers(snapshot, state);
  enriched = await resolveAccurateCalculatorSnapshot(enriched, CALCULATOR_CONTAINER_ID);
  if (hasCategoryBreakdown(enriched.categories)) {
    return renderFootprintChartDataUrl(enriched);
  }
  for (let attempt = 0; attempt < CHART_CAPTURE_MAX_ATTEMPTS; attempt += 1) {
    const iframeCapture = await tryCaptureIframeChart(CALCULATOR_CONTAINER_ID);
    if (iframeCapture) {
      return iframeCapture;
    }
    if (attempt === 0 || attempt === CHART_CAPTURE_MAX_ATTEMPTS - 1) {
      enriched = await enrichSnapshotFromSavedAnswers(enriched, state);
      enriched = await resolveAccurateCalculatorSnapshot(enriched, CALCULATOR_CONTAINER_ID);
      if (hasCategoryBreakdown(enriched.categories)) {
        return renderFootprintChartDataUrl(enriched);
      }
    }
    if (attempt < CHART_CAPTURE_MAX_ATTEMPTS - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, CHART_CAPTURE_RETRY_MS));
    }
  }
  return renderFootprintChartDataUrl(enriched);
};

const isEmptySuccessResponse = (err: unknown): boolean =>
  Boolean(err && typeof err === 'object' && (err as Response).status === 204);

const getCalculatorSnapshotFromState = (state: State, footprintHint?: number) => {
  if (state.calculatorSnapshot) {
    const normalized = normalizeCalculatorSnapshot(state.calculatorSnapshot);
    if (normalized.footprintMt > 0) {
      return normalized;
    }
  }
  if (footprintHint && footprintHint > 0) {
    return parseCalculatorSnapshot({ value: footprintHint });
  }
  if (state.competitionUser && state.competitionUser.footprint > 0) {
    return parseCalculatorSnapshot({ value: state.competitionUser.footprint });
  }
  return undefined;
};

const enrichSnapshotFromSavedAnswers = async (
  snapshot: CalculatorSnapshot,
  state: State
): Promise<CalculatorSnapshot> => {
  // Do not let previously saved answers overwrite a consistent live snapshot.
  if (snapshotTotalsMatch(snapshot)) {
    return snapshot;
  }
  if (!state.user) {
    return snapshot;
  }
  try {
    const response = await userApi(state).getCalculatorAnswers(state.user.id);
    const answers = response && response.calculatorAnswers
      ? (response.calculatorAnswers as unknown as Record<string, unknown>)
      : undefined;
    if (!answers) {
      return snapshot;
    }
    const merged = mergeCalculatorAnswersIntoSnapshot(snapshot, answers);
    // Keep the live headline total when saved categories disagree with it.
    if (snapshot.footprintMt > 0 && hasCategoryBreakdown(merged.categories)
      && Math.abs(categorySumMt(merged.categories) - snapshot.footprintMt) > 0.51) {
      return snapshot;
    }
    return merged;
  } catch (err) {
    return snapshot;
  }
};

export const updateCalculatorSnapshot = (raw: Record<string, unknown>) => (dispatch: Dispatch, getState: () => State) => {
  if (raw && raw.inputs && typeof raw.inputs === 'object') {
    setLatestCalculatorInputs(raw.inputs as Record<string, unknown>);
    // Eagerly refresh category bars whenever the iframe sends inputs so the share
    // modal already has a chartable snapshot (does not wait for modal open).
    const liveFootprint = normalizeCalculatorSnapshot(getState().calculatorSnapshot).footprintMt;
    fetchSnapshotFromCoolClimateApi(raw.inputs as Record<string, unknown>).then((fromApi) => {
      if (!fromApi || !hasCategoryBreakdown(fromApi.categories)) {
        return;
      }
      const stateNow = getState();
      const existing = normalizeCalculatorSnapshot(stateNow.calculatorSnapshot);
      const target = existing.footprintMt > 0
        ? existing.footprintMt
        : (liveFootprint > 0 ? liveFootprint : fromApi.footprintMt);
      const categories = target > 0
        ? scaleCategoriesToTotal(fromApi.categories, target)
        : fromApi.categories;
      dispatch(patch({
        calculatorSnapshot: {
          ...existing,
          ...fromApi,
          footprintMt: target > 0 ? target : fromApi.footprintMt,
          categories
        }
      }));
    }).catch(() => undefined);
  }
  const existing = normalizeCalculatorSnapshot(getState().calculatorSnapshot);
  const parsed = parseCalculatorSnapshot(raw);
  // Also accept result_* fields nested under inputs (some calculator payloads).
  const fromInputs = (raw && raw.inputs && typeof raw.inputs === 'object')
    ? parseCalculatorSnapshot(raw.inputs as Record<string, unknown>)
    : undefined;
  const footprintMt = parsed.footprintMt > 0
    ? parsed.footprintMt
    : (fromInputs && fromInputs.footprintMt > 0 ? fromInputs.footprintMt : existing.footprintMt);
  let categories = hasCategoryBreakdown(parsed.categories)
    ? parsed.categories
    : (fromInputs && hasCategoryBreakdown(fromInputs.categories)
      ? fromInputs.categories
      : existing.categories);
  // Keep prior bars when the live total moved slightly; scale them instead of
  // wiping (wiping left the share modal with no chart until a slow recompute).
  if (hasCategoryBreakdown(categories) && footprintMt > 0
    && Math.abs(categorySumMt(categories) - footprintMt) > 0.51) {
    categories = scaleCategoriesToTotal(categories, footprintMt);
  }
  const next: CalculatorSnapshot = {
    ...existing,
    ...parsed,
    footprintMt,
    householdSize: parsed.householdSize !== undefined
      ? parsed.householdSize
      : (fromInputs && fromInputs.householdSize !== undefined
        ? fromInputs.householdSize
        : existing.householdSize),
    categories
  };
  if (next.footprintMt <= 0 && next.householdSize === undefined
    && !hasCategoryBreakdown(next.categories)) {
    return;
  }
  dispatch(patch({ calculatorSnapshot: next }));
};

export const loadCalculatorChartPreview = () => async (
  dispatch: Dispatch,
  getState: () => State
): Promise<string> => {
  const state = getState();
  let snapshot = normalizeCalculatorSnapshot(state.calculatorSnapshot);
  for (let attempt = 0; attempt < CHART_CAPTURE_MAX_ATTEMPTS; attempt += 1) {
    snapshot = await resolveAccurateCalculatorSnapshot(snapshot, CALCULATOR_CONTAINER_ID);
    if (snapshot && snapshot.footprintMt > 0) {
      dispatch(patch({ calculatorSnapshot: snapshot }));
    }
    if (snapshot && hasCategoryBreakdown(snapshot.categories)) {
      // Bars are scaled to the live total in resolveAccurate; render immediately.
      return renderFootprintChartDataUrl(snapshot);
    }
    if (attempt < CHART_CAPTURE_MAX_ATTEMPTS - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, CHART_CAPTURE_RETRY_MS));
      snapshot = normalizeCalculatorSnapshot(getState().calculatorSnapshot);
    }
  }
  if (!snapshot || snapshot.footprintMt <= 0) {
    return '';
  }
  return resolveCalculatorChartPreviewUrl(snapshot, getState());
};

export const showCalculatorShareModal = (footprintHint?: number) => (dispatch: Dispatch, getState: () => State) => {
  const tryOpen = async (attempt: number) => {
    const state = getState();
    let calculatorSnapshot = getCalculatorSnapshotFromState(state, footprintHint);
    if (footprintHint && footprintHint > 0) {
      calculatorSnapshot = {
        ...normalizeCalculatorSnapshot(calculatorSnapshot),
        footprintMt: footprintHint
      };
    }
    calculatorSnapshot = await resolveAccurateCalculatorSnapshot(
      calculatorSnapshot,
      CALCULATOR_CONTAINER_ID
    );
    if (!calculatorSnapshot || calculatorSnapshot.footprintMt <= 0) {
      if (attempt < FOOTPRINT_MAX_ATTEMPTS) {
        window.setTimeout(() => {
          tryOpen(attempt + 1);
        }, FOOTPRINT_RETRY_MS);
      }
      return;
    }
    dispatch(patch({
      modal: Modals.CalculatorShare,
      calculatorShareOnFeed: false,
      calculatorSnapshot
    }));
    enrichSnapshotFromSavedAnswers(calculatorSnapshot, state).then(async (enriched) => {
      const accurate = await resolveAccurateCalculatorSnapshot(enriched, CALCULATOR_CONTAINER_ID);
      if (hasCategoryBreakdown(accurate.categories) || accurate.footprintMt > 0) {
        dispatch(patch({ calculatorSnapshot: accurate }));
      }
    });
  };
  tryOpen(0);
};

export const dismissCalculatorShareModal = () => patch({
  modal: Modals.None
});

/** Close the share modal and jump the iframe to the climate Summary page. */
export const returnToCalculatorFromShareModal = () => (dispatch: Dispatch) => {
  postCalculatorNavigate('/results/summary');
  dispatch(patch({
    modal: Modals.None
  }));
};

export const setCalculatorShareOnFeed = (shareOnFeed: boolean) => patch({
  calculatorShareOnFeed: shareOnFeed
});

const uploadChartImage = async (state: State, dataURL: string): Promise<string | undefined> => {
  const blob = await dataURLToBlob({
    dataURL,
    width: 640,
    height: 320,
    filename: 'footprint-chart.png',
    mimeType: 'image/png'
  });
  if (!blob) {
    return undefined;
  }
  const formData = new FormData();
  formData.append('image', blob, 'footprint-chart.png');
  const basePath = userApiBasePath(state);
  const response = await postFormData<{ url: string }>(`${basePath}/image`, formData, state.token);
  let url = response.url;
  // Staging STUB_GCS historically emitted localhost URLs; rewrite to the live API origin.
  if (url && /https?:\/\/localhost(?::\d+)?\//i.test(url)) {
    const apiOrigin = basePath.replace(/\/v1\/?$/, '');
    url = url.replace(/https?:\/\/localhost(?::\d+)?/i, apiOrigin);
  }
  return url;
};

export const completeCalculatorWithShareChoice = (
  shareOnFeed: boolean,
  reflectionText: string = ''
) =>
  async (dispatch: Dispatch, getState: () => State, extra: ExtraArguments) => {
    const state = getState();
    const { user, competition, calculatorSnapshot } = state;
    if (!user || !calculatorSnapshot) {
      return;
    }
    const trimmedReflection = (reflectionText || '').trim();
    if (trimmedReflection.length < STORY_MIN_CHARS) {
      toast({
        title: 'Reflection required',
        message: `Write a short reflection (at least ${STORY_MIN_CHARS} characters) to earn points and post to the Challenge.`,
        style: StyleState.Warning
      })(dispatch, getState);
      return;
    }
    const pledge = findPledgeByUUID(state, { uuid: CompetitionPledges.TakeCalculator });
    if (!pledge) {
      handleUnexpectedError(new Error('Calculator pledge not found'))(dispatch, getState);
      return;
    }
    const alreadyEarnedPoints = calculatorAvailablePoints(state) === 0 || isCalculatorPledgeComplete(state);
    const alreadyPostedReflection = (state.stories || []).some(
      (s) => s.createdBy === user.id && s.competitionPledgeID === pledge.id
    );
    if (alreadyEarnedPoints || alreadyPostedReflection) {
      toast({
        title: 'Already completed',
        message: 'You already submitted your calculator reflection. You can only share it once.',
        style: StyleState.Warning
      })(dispatch, getState);
      dispatch(patch({ modal: Modals.None, spinner: false }));
      return;
    }
    const api = userApi(state);
    const enrichedSnapshot = await enrichSnapshotFromSavedAnswers(
      normalizeCalculatorSnapshot(calculatorSnapshot),
      state
    );
    const accurateSnapshot = await resolveAccurateCalculatorSnapshot(
      enrichedSnapshot,
      CALCULATOR_CONTAINER_ID
    );
    dispatch(patch({ spinner: true, calculatorSnapshot: accurateSnapshot }));
    try {
      try {
        await api.postUserCompetitionFootprint(competition.id, {
          footprint: accurateSnapshot.footprintMt,
          footprintPublic: true
        } as any);
      } catch (err) {
        if (!isEmptySuccessResponse(err)) {
          throw err;
        }
      }

      // Optional chart image — never block the reflection story if upload fails.
      let photoUrl: string | undefined;
      if (shareOnFeed) {
        try {
          const chartDataUrl = await resolveCalculatorChartPreviewUrl(accurateSnapshot, getState());
          if (chartDataUrl) {
            photoUrl = await uploadChartImage(state, chartDataUrl);
          }
        } catch (photoErr) {
          logger.error(photoErr, `Calculator chart upload failed; posting reflection without image. userID: ${user.id}`);
        }
      }

      // Post story first — CreateStoryWithPledge awards points (same path as other actions).
      // Do NOT call postCompetitionPledgePoints beforehand: that exhausted repeats and left
      // the reflection story as a best-effort follow-up that often never appeared on Home.
      const storyBody: Partial<Story> = {
        competitionPledgeID: pledge.id,
        contentMD: formatShareStoryText(accurateSnapshot, trimmedReflection),
        status: 'new' as Story['status']
      };
      if (photoUrl) {
        storyBody.photo = photoUrl;
      }
      const storyRes = await api.postCompetitionStories(competition.id, {
        story: storyBody as Story,
        taggedUserIDs: []
      });

      const latest = (getState().latestToPledge && getState().latestToPledge![pledge.id]) || [];
      const currentStories = getState().stories || [];
      dispatch(patch({
        stories: [storyRes.story, ...currentStories.filter((s) => s.id !== storyRes.story.id)],
        storyTaggedUserIDs: {
          0: [],
          ...(getState().storyTaggedUserIDs || {}),
          ...(storyRes.storyTaggedUserIDs || {})
        },
        latestToPledge: {
          ...(getState().latestToPledge || {}),
          [pledge.id]: [user.id, ...latest.filter((userID) => userID !== user.id)]
        },
        users: {
          ...getState().users,
          ...(storyRes.users || {}),
          [user.id]: user
        }
      }, ActionType.GetCompetitionStories));

      const pointsFromStory = storyRes.points;
      let awarded: CompetitionUserPoint | undefined =
        pointsFromStory && pointsFromStory.points > 0 ? pointsFromStory : undefined;

      // If the story path awarded no points (e.g. prior incomplete attempt already
      // claimed points without a story), fall back to the pledge-points endpoint once.
      if (!awarded) {
        try {
          const res = await api.postCompetitionPledgePoints(pledge.id);
          if (res.points && res.points.points > 0) {
            awarded = res.points;
          }
        } catch (pointsErr) {
          logger.error(pointsErr, `Calculator story posted but fallback points failed. userID: ${user.id}`);
        }
      }

      const markCalculatorComplete = () => {
        const completions = (getState().userPledgeCompletions && getState().userPledgeCompletions![pledge.id]) || 0;
        const nextCompletions = Math.max(completions, pledge.repeats > 0 ? pledge.repeats : 1);
        dispatch(patch({
          userPledgeCompletions: {
            ...(getState().userPledgeCompletions || {}),
            [pledge.id]: nextCompletions
          }
        }));
      };

      if (awarded) {
        await addUserPoints(awarded, pledge.id)(dispatch, getState, extra);
      }
      markCalculatorComplete();
      try {
        const refreshed = await api.getMe(undefined, competition.name);
        dispatch(patch({
          userPledgeCompletions: refreshed.userPledgeCompletions,
          userPledgePoints: refreshed.userPledgePoints,
          competitionUserScores: refreshed.competitionUserScores
        }));
      } catch (_refreshErr) {
        // Non-fatal; local completion state is already updated.
      }

      dispatch(patch({
        spinner: false,
        modal: Modals.None,
        competitionUser: {
          ...getState().competitionUser,
          footprint: accurateSnapshot.footprintMt,
          footprintPublic: true
        }
      }));
      postCalculatorNavigate('/results/summary');
      showActionCompletedToast(dispatch, getState, extra.i18n, awarded);
      trackEvent('calculator', 'create', shareOnFeed ? 'shared' : 'private');
    } catch (err) {
      dispatch(patch({ spinner: false }));
      logger.error(err, `Failed to complete calculator action. userID: ${user.id}`);
      handleUnexpectedError(err)(dispatch, getState);
      trackEvent('calculator', 'unhandledError', 'complete');
    }
  };

export const updateFootprint = (footprint: number) => async (dispatch: Dispatch, getState: () => State) => {
  const state = getState();
  const { user, competition } = state;
  if (!user || !competition) {
    return;
  }
  const api = userApi(state);
  try {
    await api.postUserCompetitionFootprint(competition.id, { footprint, footprintPublic: true } as any);
  } catch (err) {
    if (!isEmptySuccessResponse(err)) {
      logger.error(err, `Failed to update footprint: ${user.id}`);
      trackEvent('calculator', 'unhandledError', 'updateFootprint');
      return;
    }
  }
  dispatch(patch({
    competitionUser: {
      ...state.competitionUser,
      footprint,
      footprintPublic: true
    },
    calculatorSnapshot: mergeCalculatorSnapshots(
      normalizeCalculatorSnapshot(state.calculatorSnapshot),
      parseCalculatorSnapshot({ value: footprint })
    )
  }));
};

export const setFootprintProfileVisibility = (footprintPublic: boolean) => async (dispatch: Dispatch, getState: () => State) => {
  const state = getState();
  const { competition } = state;
  if (!competition) {
    return;
  }
  const api = userApi(state);
  try {
    await api.postUserCompetitionFootprint(competition.id, { footprintPublic } as any);
  } catch (err) {
    if (!isEmptySuccessResponse(err)) {
      logger.error(err, footprintPublic ? 'Failed to show footprint on profile' : 'Failed to hide footprint from profile');
      return;
    }
  }
  const update = { footprintPublic };
  dispatch(patch({
    competitionUser: state.competitionUser ? { ...state.competitionUser, ...update } : state.competitionUser,
    competitionUserView: state.competitionUserView ? { ...state.competitionUserView, ...update } : state.competitionUserView
  }));
  trackEvent('calculator', footprintPublic ? 'showFootprint' : 'hideFootprint');
};

export const hideFootprintFromProfile = () => setFootprintProfileVisibility(false);

export const showFootprintOnProfile = () => setFootprintProfileVisibility(true);

// Legacy export kept for any stale imports; completion now uses the share modal flow.
export const createCalculatorPledgePoints = showCalculatorShareModal;
